import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authedFetch, checkSignupEmail, errText } from "../lib/api";
import { VerifyEmailNotice } from "../components/VerifyEmailNotice";
import { AuthLayout } from "../components/AuthLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Gift, ShieldCheck, PauseCircle } from "lucide-react";
import { toast } from "sonner";
import { DemoVideo } from "../components/DemoVideo";
import { SpecialtyMultiSelect, SpecialtyChips, LanguageMultiSelect, LanguageChips } from "../components/SpecialtyMultiSelect";
import { SuburbAutocomplete } from "../components/SuburbAutocomplete";
import { PhotoUpload } from "../components/PhotoUpload";
import { PasswordInput } from "../components/PasswordInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AppointmentTypeSelect } from "../components/AppointmentTypeSelect";
import { normaliseExpertiseList } from "../lib/clinicalOptions";

export default function ClinicianSignup() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    firstName: searchParams.get("first_name") || "", lastName: "", profession: searchParams.get("profession") || "", specialty: "", suburb: "", state: "VIC",
    postcode: "", phone: "", ahpraNumber: "", email: searchParams.get("email") || "", password: "", confirmPassword: "",
    gender: "", languages: "English", appointmentTypes: [], photoUrl: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailConflict, setEmailConflict] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setEmailConflict(false);
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (form.password !== form.confirmPassword) { setError("Passwords do not match"); return; }
    if (!form.profession) { setError("Please select your profession."); return; }
    if (!form.ahpraNumber.trim()) { setError("Please enter your AHPRA registration number."); return; }
    if (!form.specialty.trim()) { setError("Please select at least one specialty."); return; }
    setLoading(true);
    const emailCheck = await checkSignupEmail(form.email);
    if (!emailCheck.ok) {
      setLoading(false);
      setError(emailCheck.message);
      setEmailConflict(emailCheck.code === "EMAIL_IN_USE");
      return;
    }
    const { data, error: signErr } = await signUp({
      email: form.email, password: form.password, firstName: form.firstName, lastName: form.lastName, role: "clinician",
      profession: form.profession, specialty: form.specialty, suburb: form.suburb, state: form.state,
      postcode: form.postcode, phone: form.phone, ahpraNumber: form.ahpraNumber.trim(),
      gender: form.gender, languages: form.languages, appointmentTypes: form.appointmentTypes, photoUrl: form.photoUrl,
    });
    if (signErr) { setLoading(false); const m = errText(signErr, "Sign up failed. Please try again."); setError(/already|registered|exists/i.test(m) ? "This email is already registered. Please sign in instead." : m); toast.error(m); return; }
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      setLoading(false);
      setError("This email is already registered. Please sign in instead.");
      return;
    }
    if (!data?.session) {
      setLoading(false);
      setVerifyEmail(form.email);
      return;
    }
    const res = await authedFetch("/onboard/clinician", {
      method: "POST",
      body: JSON.stringify({
        first_name: form.firstName, last_name: form.lastName, profession: form.profession,
        specialty: form.specialty, suburb: form.suburb, state: form.state, postcode: form.postcode,
        phone: form.phone, ahpra_number: form.ahpraNumber.trim(),
        clinical_expertise: normaliseExpertiseList(form.specialty.split(",")), appointment_types: form.appointmentTypes,
        gender: form.gender, languages: form.languages, photo_url: form.photoUrl,
      }),
    });
    setLoading(false);
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      const m = errText(d.detail, "Could not create your clinician profile. Please try again.");
      setError(m);
      toast.error(m);
      return;
    }
    toast.success("Profile created! It's pending AHPRA verification.");
    navigate("/clinician", { replace: true });
  };

  return (
    verifyEmail ? <VerifyEmailNotice email={verifyEmail} testId="clinician-verify-email" /> :
    <AuthLayout eyebrow="Individual Practitioner" title="Join the directory" subtitle="Get referrals from GPs across your area." testId="clinician-signup-page">
      <div className="mb-5 space-y-2">
        <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3" data-testid="trial-callout">
          <Gift className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <p className="text-sm font-semibold text-emerald-800">3 months free. Plans start from $39/month after trial. No card required.</p>
        </div>
        <div className="flex items-start gap-3 rounded-md border border-violet-200 bg-violet-50 p-3" data-testid="pause-billing-callout">
          <PauseCircle className="mt-0.5 h-5 w-5 shrink-0 text-violet-600" />
          <p className="text-sm text-violet-800"><span className="font-semibold">Subscription pauses when your books are closed</span>, so you only pay when you are looking to take on new patients.</p>
        </div>
        <div className="flex items-start gap-3 rounded-md border border-sky-200 bg-sky-50 p-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
          <p className="text-sm text-sky-800">All clinicians are <span className="font-semibold">AHPRA-verified</span> before appearing in the GP directory.</p>
        </div>
        <DemoVideo src="/videos/clinician.mp4" title="How clinicians use SimplyRefer" buttonLabel="See how it works (60s)" className="w-full" testId="clinician-signup-demo" />
      </div>
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="clinician-signup-form">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label>First name</Label><Input required value={form.firstName} onChange={set("firstName")} placeholder="First name" data-testid="clinician-signup-firstname" /></div>
          <div className="space-y-2"><Label>Last name</Label><Input required value={form.lastName} onChange={set("lastName")} placeholder="Last name" data-testid="clinician-signup-lastname" /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
                <Label>Profession</Label>
                <Select value={form.profession} onValueChange={(value) => setForm((current) => ({ ...current, profession: value, specialty: "" }))}>
                  <SelectTrigger data-testid="clinician-signup-profession">
                    <SelectValue placeholder="Select profession" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="Psychologist">Psychologist</SelectItem>
                    <SelectItem value="Psychiatrist">Psychiatrist</SelectItem>
                  </SelectContent>
                </Select>
              </div>
          <div className="space-y-2">
            <Label>Specialties <span className="font-normal text-slate-400">(tick any that apply)</span></Label>
            <SpecialtyMultiSelect value={form.specialty} profession={form.profession} onChange={(csv) => setForm((f) => ({ ...f, specialty: csv }))} />
          </div>
        </div>
        {form.specialty.trim() && (
          <SpecialtyChips value={form.specialty} onRemove={(s) => setForm((f) => ({ ...f, specialty: f.specialty.split(",").map((x) => x.trim()).filter((x) => x && x !== s).join(", ") }))} />
        )}
        <div className="space-y-2">
          <Label>Appointment type</Label>
          <AppointmentTypeSelect value={form.appointmentTypes} onChange={(value) => setForm((current) => ({ ...current, appointmentTypes: value }))} testId="clinician-signup-appointment-types" />
          <p className="text-xs text-slate-500">Choose either or both. This is optional and only helps GPs narrow their search.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ahpra">AHPRA registration number</Label>
          <Input id="ahpra" required value={form.ahpraNumber}
            onChange={set("ahpraNumber")}
            placeholder="Enter your AHPRA registration number" data-testid="clinician-signup-ahpra" />
          <p className="text-xs text-slate-400">Enter the registration number shown on the AHPRA register so we can verify your registration before you go live.</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Suburb</Label><SuburbAutocomplete required value={form.suburb} onChange={(value) => setForm((f) => ({ ...f, suburb: value }))} onSelect={(option) => setForm((f) => ({ ...f, suburb: option.suburb, postcode: option.postcode, state: option.state }))} placeholder="Start typing a suburb or postcode" testId="clinician-signup-suburb" /></div>
          <div className="space-y-2"><Label>Postcode</Label><Input value={form.postcode} onChange={set("postcode")} placeholder="Postcode" data-testid="clinician-signup-postcode" /></div>
        </div>
        <div className="space-y-2"><Label>Phone <span className="font-normal text-slate-400">(optional)</span></Label><Input value={form.phone} onChange={set("phone")} placeholder="Contact phone number" data-testid="clinician-signup-phone" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Gender <span className="font-normal text-slate-400">(optional)</span></Label>
            <Select value={form.gender} onValueChange={(v) => setForm((f) => ({ ...f, gender: v }))}>
              <SelectTrigger data-testid="clinician-signup-gender"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Non-binary">Non-binary</SelectItem>
                <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Languages <span className="font-normal text-slate-400">(optional)</span></Label>
            <LanguageMultiSelect value={form.languages} onChange={(csv) => setForm((f) => ({ ...f, languages: csv }))} testId="clinician-signup-languages" />
            <LanguageChips value={form.languages} onRemove={(language) => setForm((f) => ({ ...f, languages: f.languages.split(",").map((item) => item.trim()).filter((item) => item && item !== language).join(", ") }))} />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Profile photo <span className="font-normal text-slate-400">(optional)</span></Label>
          <PhotoUpload value={form.photoUrl} onChange={(url) => setForm((f) => ({ ...f, photoUrl: url }))} testId="clinician-signup-photo" />
        </div>
        <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="clinician-signup-email" /></div>
        <div className="space-y-2"><Label htmlFor="password">Password</Label><PasswordInput id="password" required value={form.password} onChange={set("password")} placeholder="At least 6 characters" testId="clinician-signup-password" /></div>
        <div className="space-y-2"><Label htmlFor="confirmPassword">Confirm password</Label><PasswordInput id="confirmPassword" required value={form.confirmPassword} onChange={set("confirmPassword")} placeholder="Re-enter your password" testId="clinician-signup-confirm-password" /></div>
        {error && (
          <div className="space-y-2 text-sm text-destructive" data-testid="clinician-signup-error">
            <p>{error}</p>
            {emailConflict && (
              <p><Link className="underline" to="/login">Log in to your existing account</Link>{" or "}<Link className="underline" to="/clinic/signup">set this up as a clinic with multiple clinicians</Link>.</p>
            )}
          </div>
        )}
        <Button type="submit" className="w-full" disabled={loading} data-testid="clinician-signup-submit">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Start free trial
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        Already registered?{" "}
        <Link to="/login" className="font-medium text-primary hover:underline" data-testid="go-to-clinician-login">Sign in</Link>
      </p>
    </AuthLayout>
  );
}
